import React, { useState, useEffect, useRef } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ReactSVG } from "react-svg";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CalendarDateOptions } from "../../components/patient/create/options";

const TEXT_DATE_OPTIONS = [
  "Date Not Known",
  "Date Not Specified", 
  "Less than 3 months",
  "3 to 6 months",
  "6 months to 1 year",
  "Greater than one year",
];

// Helper functions
const parseDateValue = (value, dateOptionValue) => {
  // Return early with null values for null inputs
  if (!value && !dateOptionValue) {
    return { dateOption: null, date: null, calendarType: null };
  }

  if (dateOptionValue) {
    if (TEXT_DATE_OPTIONS.includes(dateOptionValue)) {
      return { dateOption: dateOptionValue, date: null, calendarType: null };
    }

    if (dateOptionValue === "(YYYY)") {
      const dateObj = value ? dayjs(value, "YYYY") : null;
      if (dateObj?.isValid()) {
        return { dateOption: "(YYYY)", date: dateObj, calendarType: "year" };
      }
      return { dateOption: "(YYYY)", date: null, calendarType: "year" };
    }
    if (dateOptionValue === "(MM/YYYY)") {
      const dateObj = value ? dayjs(value, "YYYY-MM") : null;
      if (dateObj?.isValid()) {
        return { dateOption: "(MM/YYYY)", date: dateObj, calendarType: "month" };
      }
      return { dateOption: "(MM/YYYY)", date: null, calendarType: "month" };
    }
    if (dateOptionValue === "(DD/MM/YYYY)") {
      const dateObj = value ? dayjs(value, "YYYY-MM-DD") : null;
      if (dateObj?.isValid()) {
        return { dateOption: "(DD/MM/YYYY)", date: dateObj, calendarType: "date" };
      }
      return { dateOption: "(DD/MM/YYYY)", date: null, calendarType: "date" };
    }
  }

  if (value) {
    const dateObj = dayjs(value);
    if (dateObj.isValid()) {
      if (value.match(/^\d{4}$/)) return { dateOption: "(YYYY)", date: dateObj, calendarType: "year" };
      if (value.match(/^\d{4}-\d{2}$/)) return { dateOption: "(MM/YYYY)", date: dateObj, calendarType: "month" };
      return { dateOption: "(DD/MM/YYYY)", date: dateObj, calendarType: "date" };
    }
  }

  return { dateOption: null, date: null, calendarType: null };
};

const formatDisplayDate = (date, calendarType) => {
  if (!date) return "";
  if (calendarType === "year") return date.format("YYYY");
  if (calendarType === "month") return date.format("MMM YYYY");
  return date.format("DD MMM YYYY");
};

const getDatePattern = (calendarType) => {
  if (calendarType === "year") return ["YYYY", "(YYYY)"];
  if (calendarType === "month") return ["YYYY-MM", "(MM/YYYY)"];
  return ["YYYY-MM-DD", "(DD/MM/YYYY)"];
};

// Dropdown Options Component
const DateOptionsDropdown = ({ options, selectedValue, onSelect, isOpen }) => {
  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full z-50 mt-1 w-72 max-h-72 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
      {options.map((option) => (
        <div
          key={option.value}
          className={`cursor-pointer px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 ${
            selectedValue === option.value ? "bg-teal-50" : ""
          }`}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
};

/**
 * DateWithOptionsInput - A date input with dropdown options for timeframe selection
 * @param {Object} props - Component props
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 * @param {string} props.value - Current date value (YYYY-MM-DD, YYYY-MM, or YYYY format)
 * @param {string} props.dateOptionValue - Current date option value (from CalendarDateOptions)
 * @param {Function} props.onDateOptionChange - Callback when date/option changes: (dateValue, dateOptionValue) => {}
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.inputClassName - Additional CSS classes for input
 */
const DateWithOptionsInput = ({
  name,
  id,
  value,
  dateOptionValue,
  onDateOptionChange,
  placeholder,
  disabled = false,
  inputClassName = "",
  isIntervention = false,
}) => {
  const [selectedDateOption, setSelectedDateOption] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showDateCalendar, setShowDateCalendar] = useState(false);
  const [dateCalendarType, setDateCalendarType] = useState(null);
  const dateWrapperRef = useRef(null);

  // Get popup container for calendar
  const getPopupContainer = (trigger) => {
    // Return the date wrapper element to contain the calendar popup
    return dateWrapperRef.current;
  };

  // Parse state from props
  const parsedState = parseDateValue(value, dateOptionValue);

  // Update state from parsed props - use direct comparison to prevent re-renders
  useEffect(() => {
    setSelectedDateOption(parsedState.dateOption);
    setSelectedDate(parsedState.date);
    setDateCalendarType(parsedState.calendarType);
    if (!parsedState.dateOption) {
      setShowDateCalendar(false);
    }
  }, [value, dateOptionValue]);

  // Handle outside click
  useEffect(() => {
    if (showDateDropdown || showDateCalendar) {
      const handleClickOutside = (event) => {
        const isCalendarPopup = event.target.closest('.ant-picker-dropdown') || 
                               event.target.closest('.ant-picker-panel');
        
        if (dateWrapperRef.current && 
            !dateWrapperRef.current.contains(event.target) && 
            !isCalendarPopup) {
          setShowDateDropdown(false);
          setShowDateCalendar(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDateDropdown, showDateCalendar]);

  // Calendar Picker Component  
  const CalendarPicker = ({ calendarType, value, onChange, isOpen }) => {
    if (!isOpen || !calendarType) return null;

    const commonProps = {
      value,
      onChange,
      open: isOpen,
      placement: "bottomLeft",
      getPopupContainer,
    };

    if (calendarType === "year") return <DatePicker picker="year" format="YYYY" {...commonProps} />;
    if (calendarType === "month") return <DatePicker picker="month" format="MMM YYYY" {...commonProps} />;
    return <DatePicker format="YYYY-MM-DD" {...commonProps} />;
  };

  // Get display values
  const optionLabel = selectedDateOption && CalendarDateOptions.find((o) => o.value === selectedDateOption)?.label;
  const displayDate = formatDisplayDate(selectedDate, dateCalendarType);

  // Calculate input value
  const inputValue = selectedDateOption && TEXT_DATE_OPTIONS.includes(selectedDateOption)
    ? optionLabel || ""
    : selectedDate && displayDate
    ? displayDate
    : optionLabel || "";

  // Handle dropdown option selection
  const handleDateOptionChange = (optionValue) => {
    setSelectedDateOption(optionValue);
    setShowDateDropdown(false);

    if (TEXT_DATE_OPTIONS.includes(optionValue)) {
      setShowDateCalendar(false);
      setSelectedDate(null);
      setDateCalendarType(null);
      onDateOptionChange?.(null, optionValue);
      return;
    }

    const calendarType = optionValue === "(YYYY)" ? "year" : 
                        optionValue === "(MM/YYYY)" ? "month" : "date";
    setDateCalendarType(calendarType);
    setShowDateCalendar(true);
    onDateOptionChange?.(null, optionValue);
  };

  // Handle calendar date selection
  const handleDateChange = (dateObj) => {
    if (!dateObj || !dateObj.isValid()) {
      setSelectedDate(null);
      onDateOptionChange?.(null, selectedDateOption);
      return;
    }

    setSelectedDate(dateObj);
    setShowDateCalendar(false);
    
    const [pattern, opt] = getDatePattern(dateCalendarType);
    onDateOptionChange?.(dateObj.format(pattern), opt);
  };

  // Handle clear date
  const handleClearDate = () => {
    setSelectedDate(null);
    setSelectedDateOption(null);
    setDateCalendarType(null);
    setShowDateCalendar(false);
    onDateOptionChange?.(null, null);
  };

  // Handle calendar icon click
  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    if (!["(YYYY)", "(MM/YYYY)", "(DD/MM/YYYY)"].includes(selectedDateOption)) {
      setSelectedDateOption("(DD/MM/YYYY)");
      setDateCalendarType("date");
      onDateOptionChange?.(null, "(DD/MM/YYYY)");
    }
    setShowDateDropdown(false);
    setShowDateCalendar(true);
  };

  // Handle chevron click
  const handleChevronClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setShowDateDropdown(!showDateDropdown);
      setShowDateCalendar(false);
    }
  };

  return (
    <div ref={dateWrapperRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          name={name}
          id={id}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly
          className={`w-full border-0 bg-white px-0 py-0 pr-12 ${inputClassName} focus:outline-none focus:ring-0 ${
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
        />
        <div className={`absolute ${isIntervention ? 'right-0' : 'right-2'} top-1/2 flex -translate-y-1/2 items-center gap-3 z-10`}>
        {(selectedDate || selectedDateOption) && (
            <>
              <button
                type="button"
                className="flex cursor-pointer items-center justify-center"
                onClick={handleClearDate}
              >
                <XMarkIcon className="h-4 w-4 text-gray-600" />
              </button>
              <span className="h-6 w-px bg-gray-200" />
            </>
          )}
          <button
            type="button"
            className="flex cursor-pointer items-center justify-center"
            onClick={handleCalendarClick}
          >
            <ReactSVG src="/icons/medicalSummary/Notes/CalendarIcon.svg" className="h-5 w-5" />
          </button>
          <span className="h-6 w-px bg-gray-200" />
          <ChevronDownIcon
            className={`${isIntervention ? 'h-5 w-5' : 'h-4 w-4'} cursor-pointer ${isIntervention ? 'text-gray-800' : 'text-gray-500'} stroke-current`}
            strokeWidth={isIntervention ? 1.5 : 3}
            onClick={handleChevronClick}
          />
        </div>
      </div>
      <DateOptionsDropdown
        options={CalendarDateOptions}
        selectedValue={selectedDateOption}
        onSelect={handleDateOptionChange}
        isOpen={showDateDropdown}
      />
      <div className="absolute right-0 top-full z-[60] mt-2 opacity-0 pointer-events-none">
      <CalendarPicker
        calendarType={dateCalendarType}
          value={selectedDate}
          onChange={handleDateChange}
          isOpen={showDateCalendar && !!selectedDateOption}
        />
    </div>
    </div>
  );
};

export default DateWithOptionsInput;
